import { useCallback, useEffect, useRef, useState } from 'react';
import {
  streamInterview,
  type InterviewInitType,
  type InterviewStatus,
  type InterviewStreamController,
  type InterviewStreamPayload,
} from '@/api/interviewApi';
import { SSEStreamStatus, type SSEMetadataData, type SSEToolCallData, type SSEToolResultData } from '@/types/sse.types';
import type { PrepareInterviewResponse } from '@/api/interviewApi';

type SystemPromptState = PrepareInterviewResponse['systemPromptStatus'];

type SessionInitLabel = InterviewInitType | 'message' | null;

type InterviewSessionState = {
  status: SSEStreamStatus;
  lastError: string | null;
  lastInitType: SessionInitLabel;
};

const startingInterviews = new Set<string>();

const START_FLAG_PREFIX = 'interview_started_';

const getStartedFlag = (interviewId: string | undefined): boolean => {
  if (typeof window === 'undefined' || !interviewId) return false;
  try {
    return window.localStorage.getItem(`${START_FLAG_PREFIX}${interviewId}`) === '1';
  } catch {
    return false;
  }
};

const setStartedFlag = (interviewId: string | undefined) => {
  if (typeof window === 'undefined' || !interviewId) return;
  try {
    window.localStorage.setItem(`${START_FLAG_PREFIX}${interviewId}`, '1');
  } catch {
    // Ignore storage errors
  }
};

type UseInterviewSessionOptions = {
  interviewId?: string;
  initialInterviewStatus?: InterviewStatus;
  candidateName?: string;
  systemPromptStatus?: SystemPromptState;
  preparedInSession?: boolean;
  onChunk?: (text: string) => void;
  onComplete?: (fullText: string) => void;
  onStreamError?: (message: string) => void;
  onMetadata?: (data: SSEMetadataData) => void;
  onToolCall?: (data: SSEToolCallData) => void;
  onToolResult?: (data: SSEToolResultData) => void;
};

type SendUserMessageParams = {
  message: string;
  timestamp: string;
};

export function useInterviewSession(options: UseInterviewSessionOptions) {
  const {
    interviewId,
    initialInterviewStatus,
    candidateName,
    systemPromptStatus,
    preparedInSession,
    onChunk,
    onComplete,
    onStreamError,
    onMetadata,
    onToolCall,
    onToolResult,
  } = options;

  const [state, setState] = useState<InterviewSessionState>({
    status: SSEStreamStatus.IDLE,
    lastError: null,
    lastInitType: null,
  });

  const statusRef = useRef<SSEStreamStatus>(SSEStreamStatus.IDLE);
  const isMountedRef = useRef(true);
  const streamRef = useRef<InterviewStreamController | null>(null);
  const suppressAbortStateRef = useRef(false);
  const streamBufferRef = useRef<string>('');

  const updateState = useCallback(
    (updater: (prev: InterviewSessionState) => InterviewSessionState) => {
      if (!isMountedRef.current) return;
      setState((prev) => {
        const next = updater(prev);
        statusRef.current = next.status;
        return next;
      });
    },
    [],
  );

  const stopStream = useCallback((suppressAbortState = false) => {
    if (streamRef.current) {
      suppressAbortStateRef.current = suppressAbortState;
      streamRef.current.abort();
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopStream();
    };
  }, [stopStream]);

  useEffect(() => {
    // Reset guards and state when interview changes
    statusRef.current = SSEStreamStatus.IDLE;
    updateState(() => ({
      status: SSEStreamStatus.IDLE,
      lastError: null,
      lastInitType: null,
    }));
  }, [interviewId, updateState]);

  const openStream = useCallback(
    async (payload: InterviewStreamPayload, initLabel: SessionInitLabel) => {
      if (!payload.interview_id) {
        updateState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ERROR,
          lastError: 'Missing interview id',
          lastInitType: initLabel,
        }));
        return;
      }

      // Avoid double updates from aborting a previous connection
      stopStream(true);

      updateState((prev) => ({
        ...prev,
        status: SSEStreamStatus.CONNECTING,
        lastError: null,
        lastInitType: initLabel,
      }));

      try {
        streamRef.current = await streamInterview(payload, {
          onConnected: (data) => {
            if ((initLabel === 'start' || initLabel === 'resume') && interviewId) {
              setStartedFlag(interviewId);
            }
            if (interviewId) {
              startingInterviews.delete(interviewId);
            }
            updateState((prev) => ({
              ...prev,
              status: SSEStreamStatus.STREAMING,
            }));
          },
          onChunk: (data) => {
            streamBufferRef.current += data.text;
            onChunk?.(data.text);
          },
          onComplete: (data) => {
            const fullText = streamBufferRef.current;
            streamBufferRef.current = '';
            onComplete?.(fullText);
            if (interviewId) {
              startingInterviews.delete(interviewId);
            }
            updateState((prev) => ({
              ...prev,
              status: SSEStreamStatus.COMPLETED,
            }));
          },
          onError: (data) => {
            const fullText = streamBufferRef.current;
            streamBufferRef.current = '';
            onStreamError?.(data?.message || 'Unknown stream error');
            if (fullText) {
              onComplete?.(fullText);
            }
            if (interviewId) {
              startingInterviews.delete(interviewId);
            }
            updateState((prev) => ({
              ...prev,
              status: SSEStreamStatus.ERROR,
              lastError: data?.message || 'Unknown stream error',
            }));
          },
          onMetadata: (data) => {
            onMetadata?.(data as SSEMetadataData);
          },
          onToolCall: (data) => {
            onToolCall?.(data as SSEToolCallData);
          },
          onToolResult: (data) => {
            onToolResult?.(data as SSEToolResultData);
          },
          onAbort: () => {
            if (suppressAbortStateRef.current) {
              suppressAbortStateRef.current = false;
              return;
            }
            streamBufferRef.current = '';
            if (interviewId) {
              startingInterviews.delete(interviewId);
            }
            updateState((prev) => ({
              ...prev,
              status: SSEStreamStatus.ABORTED,
            }));
          },
        });
      } catch (error) {
        if (interviewId) {
          startingInterviews.delete(interviewId);
        }
        streamBufferRef.current = '';
        const message = error instanceof Error ? error.message : 'Failed to start interview stream';
        onStreamError?.(message);
        updateState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ERROR,
          lastError: message,
        }));
      }
    },
    [stopStream, updateState],
  );

  const startInitialStream = useCallback(() => {
    if (
      statusRef.current === SSEStreamStatus.CONNECTING ||
      statusRef.current === SSEStreamStatus.STREAMING
    ) {
      return;
    }
    if (!interviewId) {
      updateState((prev) => ({
        ...prev,
        status: SSEStreamStatus.ERROR,
        lastError: 'Interview id missing',
      }));
      return;
    }

    const alreadyStartedFlag = getStartedFlag(interviewId);

    // Determine whether to start or resume
    let initType: InterviewInitType | undefined;
    if (alreadyStartedFlag) {
      initType = 'resume';
    } else if (preparedInSession) {
      initType = 'start';
    } else if (systemPromptStatus === 'preparing') {
      initType = 'start';
    } else if (initialInterviewStatus === 'not_started') {
      initType = 'start';
    } else if (initialInterviewStatus === 'in_progress') {
      initType = 'resume';
    } else if (!initialInterviewStatus) {
      initType = 'start';
    }

    if (!initType) {
      return;
    }

    if (interviewId && startingInterviews.has(interviewId)) {
      return;
    }
    if (interviewId) {
      startingInterviews.add(interviewId);
    }

    streamBufferRef.current = '';

    void openStream(
      {
        interview_id: interviewId,
        interview_init_type: initType,
        candidate_name: candidateName,
      },
      initType,
    );
  }, [candidateName, initialInterviewStatus, interviewId, openStream, systemPromptStatus, preparedInSession]);

  const sendUserMessage = useCallback(
    async ({ message, timestamp }: SendUserMessageParams) => {
      if (!interviewId) {
        updateState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ERROR,
          lastError: 'Interview id missing',
        }));
        return;
      }

      if (!message.trim() || !timestamp.trim()) {
        updateState((prev) => ({
          ...prev,
          status: SSEStreamStatus.ERROR,
          lastError: 'Message and timestamp are required',
        }));
        return;
      }

      await openStream(
        {
          interview_id: interviewId,
          user_message: message,
          timestamp,
          candidate_name: candidateName,
        },
        'message',
      );
    },
    [candidateName, interviewId, openStream, updateState],
  );

  return {
    state,
    startInitialStream,
    sendUserMessage,
    stopStream,
    isStreaming: state.status === SSEStreamStatus.STREAMING,
  };
}
